import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Link, IconButton, InputLabel, OutlinedInput, Stack, SvgIcon, Typography, TextField, FormControl, InputAdornment, Divider } from '@mui/material';
import { setApplication } from 'api/application';
import { useSnackbar } from 'components/snackbar';
import { getTimeDifferenceString, changeNameFormat, formatNumber, toTitleCase } from 'utils/Utils';

/**
 * ExternalJob is a React component that facilitates job application processes for external job listings.
 * It provides options for users to continue with an application on an external platform and download their resume.
 * 
 * Props:
 * - onClose: A function that is called to close the dialog.
 * - externalJobStatus: A function that updates the job status externally.
 * - downloadResume: A function that allows users to download their resume.
 * - job: An object containing job details.
 * - tab: A string indicating the current tab context.
 * - candidateDetails: An object containing the candidate's details.
 * - matchingSkills: An array of skills that match the job requirements.
 * 
 * The component displays a dialog with options to continue the application on an external link or download a resume.
 * It manages the submission state and provides notifications on the application progress.
 */

const ExternalJob = ({ onClose, externalJobStatus, downloadResume, job, tab, candidateDetails, matchingSkills }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Opens the external job application link in a new window and initiates
   * the process to mark the job as in-progress.
   * 
   * Determines the correct external link based on the current tab context
   * and opens it using `window.open`. Then, it calls `handleJobInprogress`
   * to update the status of the job application.
   */

  const handleContinueWithApplication = () => {
    const externalLink = tab === '2' ? job?.external_link : job?.jobDetails?.external_link;
    window.open(externalLink);
    handleJobInprogress();
  };
  /**
   * Updates the status of an external job application and provides notifications.
   *
   * This function handles the process of marking an external job application as
   * in-progress. It updates the application status based on the current tab context,
   * submits the application details to the server, and displays appropriate
   * notifications for success or failure.
   *
   * If the application submission is successful, it updates the job status and
   * invokes the `externalJobStatus` function to reflect the change. It also triggers
   * the `onClose` callback to close the dialog.
   *
   * If the submission fails for any reason, an error notification is shown.
   *
   * @async
   * @returns {Promise<void>}
   */

  const handleJobInprogress = async () => {
    try {
      setIsSubmitting(true);
      if (candidateDetails && matchingSkills && job) {
        const jobDetails = tab === '2' ? job : job?.jobDetails;
        const response = await setApplication({ candidateDetails, matchingSkills, jobDetails, status: 'application pending', tj_score: job?.tj_score });
        if (response?.success && response?.data != null) {
          const company = response?.data?.job_id?.company;
          const title = response?.data?.job_id?.title;
          enqueueSnackbar(`Your application to ${toTitleCase(company)} for the ${title} role is in progress`, { variant: 'info' });
          if (tab === '3') {
            job.status = response?.data?.status;
            job.applied_at = response?.data?.applied_at;
          } else {
            job.application_status = response?.data?.status;
          }
          // externalJobStatus();
          onClose('open External Job Dialog');
        } else {
          enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to submit the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="ext-job-dialog-wraper">
      <Stack justifyContent={'center'}>
        <Box sx={{ textAlign: 'center' }} mb={3}>
          <Typography className="title" mb={0.5}>External Job Listing</Typography>
          <Typography className="info-text">You’ll be redirected to the company’s site. Please have your resume ready.</Typography>
        </Box>
        <Stack direction={{ xs: 'column-reverse', md: 'row' }} alignItems={'center'} spacing={2.5}>
          <Button fullWidth variant="outlined" size="medium" onClick={handleContinueWithApplication} disabled={isSubmitting}>
            {isSubmitting ? 'In-progress...' : 'Continue with application'}
          </Button>
          <Button fullWidth variant="contained" size="medium" onClick={downloadResume}>
            Select & Download Resume
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

export default ExternalJob;
